import React from 'react';
import { useTranslation } from 'react-i18next';

// Enumerable
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { ButtonType } from '../../../types/ButtonType';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { Namespace } from '../../../translation/namespace';

// Components
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { SVGIcon } from '../../../components/atoms/SVG';

type PageManagementActionProps<T extends string> = {
  viewType: T;
  href: string;
  onClick: (e: T) => void;
  accessKey?: string;
};

export function PageManagementActionLink<T extends string>({
  viewType,
  href,
  onClick,
  accessKey,
}: PageManagementActionProps<T>) {
  const { t } = useTranslation(Namespace.PageManagement);

  return (
    <li className="pagemanagement__action-list-item">
      {/* if used correctly and wisely, accesskeys are a11y enhancement */}
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <Hyperlink
        to={href}
        className="pagemanagement__action-link font-regular-15-21"
        routing={false}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          onClick(viewType);
        }}
        accessKey={accessKey}
        ariaHaspopup
      >
        {t(`views.${viewType}.text`)}
      </Hyperlink>
    </li>
  );
}

type PageManagementActionButtonProps<T extends string> = PageManagementActionProps<T> & {
  icon: SVGIcon;
  active: boolean;
  title: string;
};

export function PageManagementActionButton<T extends string>({
  viewType,
  href,
  active,
  onClick,
  accessKey,
  icon,
  title,
}: PageManagementActionButtonProps<T>) {
  return (
    <li className="pagemanagement__button-container">
      {/* if used correctly and wisely, accesskeys are a11y enhancement */}
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <Btn
        href={href}
        type={ButtonType.BUTTON}
        svgSpriteId={icon}
        text={title}
        titleText={title}
        hiddenText
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          onClick(viewType);
        }}
        tooltipPlacement={TooltipPlacement.TOP}
        modifier={active ? BtnModifier.ACTIVE : undefined}
        accessKey={accessKey}
        ariaHaspopup
      />
    </li>
  );
}
